import React, { useState, useEffect } from "react";
import { getServiceCatalog, requestService, getMyServiceRequests } from "../../api";
import { ShieldCheck, UserCheck, FileCheck2, Banknote, Landmark, BellRing, Loader2, Check, Clock, X } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const META = {
  tenant_referencing: { icon: UserCheck, desc: "Full credit, affordability, employer and previous-landlord references before you let." },
  aml_right_to_rent: { icon: FileCheck2, desc: "Identity verification, Right-to-Rent and Anti-Money-Laundering checks handled for you." },
  rent_guarantee: { icon: Banknote, desc: "Rent guarantee and legal expenses cover so your income is protected if a tenant defaults." },
  rent_reconciliation: { icon: Landmark, desc: "Open-banking rent matching — every payment reconciled automatically against the tenancy." },
  compliance_reminders: { icon: BellRing, desc: "Automatic gas safety, EICR and EPC renewal reminders so you never miss a deadline." },
};
const STATUS = {
  requested: { label: "Requested", c: "bg-amber-100 text-amber-700", i: Clock },
  in_progress: { label: "In progress", c: "bg-sky-100 text-sky-700", i: Loader2 },
  complete: { label: "Complete", c: "bg-green-100 text-green-700", i: Check },
  declined: { label: "Declined", c: "bg-gray-100 text-gray-600", i: X },
};

const LandlordProtection = () => {
  const { toast } = useToast();
  const [catalog, setCatalog] = useState([]);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => Promise.all([getServiceCatalog(), getMyServiceRequests()]).then(([c, r]) => { setCatalog(c.services || []); setRequests(r || []); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const latestFor = (type) => requests.find((r) => r.serviceType === type);

  const request = async (type) => {
    setBusy(type);
    try { await requestService(type); toast({ title: "Request sent", description: "Our team will be in touch to arrange it." }); await load(); }
    catch { toast({ title: "Couldn't send request", variant: "destructive" }); }
    setBusy("");
  };

  return (
    <div className="space-y-6" data-testid="landlord-protection">
      <div>
        <h3 className="font-display text-[24px] font-300 text-[#2b2b2b] flex items-center gap-2"><ShieldCheck size={22} className="text-[#b8935f]" /> Landlord Protection &amp; Checks</h3>
        <p className="text-[14px] text-[#6a6a6a] mt-1 max-w-2xl">Request any of our background-check and protection services. We'll arrange it and keep you updated here.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {catalog.map((s) => {
          const m = META[s.type] || {};
          const Icon = m.icon || ShieldCheck;
          const req = latestFor(s.type);
          const st = req && STATUS[req.status];
          return (
            <div key={s.type} className="bg-white rounded-2xl border border-[#f0ece5] p-5 flex flex-col" data-testid={`service-card-${s.type}`}>
              <div className="flex items-start gap-3 mb-3">
                <span className="w-11 h-11 rounded-xl bg-[#faf7f2] grid place-items-center text-[#b8935f] shrink-0"><Icon size={20} /></span>
                <div>
                  <p className="text-[15px] font-500 text-[#2b2b2b]">{s.name}</p>
                  <p className="text-[13px] text-[#6a6a6a] leading-relaxed mt-0.5">{m.desc}</p>
                </div>
              </div>
              <div className="mt-auto flex items-center justify-between pt-2">
                {req ? (
                  <span className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full ${st.c}`} data-testid={`service-status-${s.type}`}><st.i size={12} className={req.status === "in_progress" ? "animate-spin" : ""} /> {st.label}</span>
                ) : <span className="text-[12px] text-[#a0a0a0]">Not requested</span>}
                <button onClick={() => request(s.type)} disabled={busy === s.type} data-testid={`service-request-${s.type}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
                  {busy === s.type ? <Loader2 size={14} className="animate-spin" /> : null} {req ? "Request again" : "Request"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {requests.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
          <p className="text-[13px] uppercase tracking-wide text-[#b8935f] mb-3">Your requests</p>
          <div className="space-y-2">
            {requests.map((r) => {
              const st = STATUS[r.status] || STATUS.requested;
              return (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid={`service-request-row-${r.id}`}>
                  <span className="text-[14px] text-[#2b2b2b] flex-1 truncate">{r.serviceName}</span>
                  {r.adminNote && <span className="text-[12px] text-[#8a8a8a] hidden sm:block truncate max-w-[240px]">{r.adminNote}</span>}
                  <span className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full ${st.c}`}><st.i size={12} className={r.status === "in_progress" ? "animate-spin" : ""} /> {st.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandlordProtection;
